import React, { useContext, useEffect, useRef, useState } from "react";
import { ActionIcon, Button, Code, Grid, Popover, Radio, Select, Spoiler, Text, TextInput, ScrollArea, Tabs, Textarea, useMantineTheme } from "@mantine/core";
import { Prism } from '@mantine/prism';
import { IconCode, IconPlus, IconTrash, IconTerminal, IconBrandJavascript, IconServer, IconDownload } from "@tabler/icons";
import { DataFlowContext } from "../../../../core/context/DataFlowContext";
import { v4 as uuid } from 'uuid';
import VariableTextarea from "../../../GlobalComponent/VariableTextarea"
import FormDataBody from "./FormDataBody";
import VariableSelectWrite from "../../../GlobalComponent/VariableSelectWrite";
import styles from "../../../GlobalStyles/styleInputSearch.module.css"

const responseBodyMapping = `
response: {
    "status": "ok",
    "result": {
        "field": "value",
    }
}
`

const responseHeadersMapping = `
{
    "Server": "nginx,
    "Vary": "Accept, Origin,
    "Content-Type": "application/json,
    ...
}
`

const responseCookiesMapping = `
{
    "i": {
        "path": "/",
        "value": "Uy/dClqOS6QjiEcSblablabla",
        "domain": "ya.ru",
        "secure": true,
        "comment": "",
        "expires": "Sat, 15-Dec-2029 23:37:51 GMT",
        "httponly": true,
        ...
    },
    "yp": {
        "path": "/",
        "value": "1579304272.ygu.1",
        "domain": "ya.ru",
        "expires": "Sat, 15-Dec-2029 23:37:51 GMT",
        ...
    }
}
`

const SetHttpRequest = (props) => {
    const [url, setUrl] = useState(props?.nodeData?.data?.options?.url ?? "")
    const [method, setMethod] = useState(props?.nodeData?.data?.options?.method ?? "POST")
    const [headers, setHeaders] = useState(props?.nodeData?.data?.options?.headers ?? "")
    const [cookies, setCookies] = useState(props?.nodeData?.data?.options?.cookies ?? "")
    const [contentType, setContentType] = useState(props?.nodeData?.data?.options?.contentType ?? "")
    const [dataString, setDataString] = useState(props?.nodeData?.data?.options?.dataString ?? "")
    const [outputBodyVariables, setOutputBodyVariables] = useState(props?.nodeData?.data?.options?.outputBodyVariables ?? "")
    const [outputHeadersVariables, setOutputHeadersVariables] = useState(props?.nodeData?.data?.options?.outputHeadersVariables ?? "")
    const [outputCookiesVariables, setOutputCookiesVariables] = useState(props?.nodeData?.data?.options?.outputCookiesVariables ?? "")
    const [openDropdown, setOpenDropdown] = useState(false)

    // Response mode state
    const [responseMode, setResponseMode] = useState(props?.nodeData?.data?.options?.responseMode ?? "json")
    const [simpleResponseVariable, setSimpleResponseVariable] = useState(props?.nodeData?.data?.options?.simpleResponseVariable ?? "")
    const [importText, setImportText] = useState('')
    const [importDropdownOpen, setImportDropdownOpen] = useState(false)
    const data = useContext(DataFlowContext)
    const formBodyRef = useRef()
    const textareaRef = useRef(null)
    const textareaRefBody = useRef(null)
    const handleDropdown = () => {
        setOpenDropdown(!openDropdown)
    }
    const handleInputValue = (value) => {
        setUrl(value)
    }
    const handleSetValue = (e) => {
        const textarea = textareaRef.current;
        const startPos = textarea.selectionStart;
        const firstPart = url.slice(0, startPos);
        const secondPart = url.slice(startPos);
        const value = e.target.getAttribute('value')
        const newValue = firstPart + "${" + value + "}" + secondPart
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(startPos + value.length + 4, startPos + value.length + 3);
        }, 0);
        setOpenDropdown(false)
        setUrl(newValue)
    }
    console.log(outputBodyVariables)

    const handleSelectContentType = (value) => {
        formBodyRef?.current?.onChangeContentType(value)
        setContentType(value)
        if (value !== 'application/x-www-form-urlencoded' && value !== 'multipart/form-data') {
            setDataString("")
        } else if (!Array.isArray(dataString)) {
            setDataString([])
        }
    }

    // handle select radio method
    const handleRadioMethod = (value) => {
        setMethod(value)
    }

    // handle change text area data string
    const handleChangeDataString = (value) => {
        setDataString(value)
    }

    // handle delete input headers
    const handleDeleteHeaders = (id) => {
        setHeaders((pre) =>
            pre.filter(header => header.id !== id))
    }

    // handle delete input cookies
    const handleDeleteCookies = (id) => {
        setCookies((pre) => pre.filter(cookie => cookie.id !== id))
    }

    // handle delete input body variable
    const handleDeleteBodyVariable = (id) => {
        setOutputBodyVariables((pre) => pre.filter(bodyVariable => bodyVariable.id !== id))

    }

    // handle delete input header variable
    const handleDeleteHeaderVariable = (id) => {
        setOutputHeadersVariables((pre) => pre.filter(headerVariable => headerVariable.id !== id))

    }

    // handle delete input cookie variable
    const handleDeleteCookieVariable = (id) => {
        setOutputCookiesVariables((pre) => pre.filter(cookieVariable => cookieVariable.id !== id))
    }

    // handle add input headers
    const handleAddHeaders = () => {
        setHeaders((pre) => [...pre, {
            id: uuid(),
            key: "",
            value: ""
        }])
    }

    // handle add input cookies
    const handleAddCookies = () => {
        setCookies((pre) => [...pre, {
            id: uuid(),
            key: "",
            value: ""
        }])
    }

    // handle add input body variable
    const handleAddBodyVariable = () => {
        setOutputBodyVariables((pre) => [...pre, {
            id: uuid(),
            key: "",
            value: ""
        }])
    }

    // handle add input header variable
    const handleAddHeaderVariable = () => {
        setOutputHeadersVariables((pre) => [...pre, {
            id: uuid(),
            key: "",
            value: ""
        }])
    }

    // handle add input header variable
    const handleAddCookieVariable = () => {
        setOutputCookiesVariables((pre) => [...pre, {
            id: uuid(),
            key: "",
            value: ""
        }])
    }

    const handleParseJsonToHeaders = (jsonString) => {
        try {
            const parsed = JSON.parse(jsonString);
            const newHeaders = Object.entries(parsed).map(([key, value]) => ({
                id: uuid(),
                key: key,
                value: String(value)
            }));
            setHeaders([...newHeaders]);
            return true;
        } catch (error) {
            console.error('Invalid JSON format:', error);
            return false;
        }
    }

    const handleParseHeaderString = (headerString) => {
        try {
            // Split by semicolon or newline and parse each header
            const headerPairs = headerString.split(/[;\n]/).map(header => header.trim()).filter(header => header.length > 0);
            console.log("headerPairs", headerPairs)
            const newHeaders = [];
            for (const headerPair of headerPairs) {
                const colonIndex = headerPair.indexOf('=');
                if (colonIndex > 0) {
                    const key = headerPair.substring(0, colonIndex).trim();
                    const value = headerPair.substring(colonIndex + 1).trim();
                    if (key && value) {
                        newHeaders.push({
                            id: uuid(),
                            key: key,
                            value: value
                        });
                    }
                }
            }

            console.log(newHeaders)

            if (newHeaders.length > 0) {
                setHeaders([...newHeaders]);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Invalid header string format:', error);
            return false;
        }
    }

    const handleParseJsonToCookies = (jsonString) => {
        try {
            const parsed = JSON.parse(jsonString);
            const newCookies = Object.entries(parsed).map(([key, value]) => ({
                id: uuid(),
                key: key,
                value: String(value)
            }));
            setCookies([...newCookies]);
            return true;
        } catch (error) {
            console.error('Invalid JSON format:', error);
            return false;
        }
    }

    const handleParseCookieString = (cookieString) => {
        try {
            // Split by semicolon and parse each cookie
            console.log("cookieString", cookieString)
            const cookiePairs = cookieString.split(';').map(cookie => cookie.trim()).filter(cookie => cookie.length > 0);
            const newCookies = [];

            for (const cookiePair of cookiePairs) {
                const equalIndex = cookiePair.indexOf('=');
                if (equalIndex > 0) {
                    const key = cookiePair.substring(0, equalIndex).trim();
                    const value = cookiePair.substring(equalIndex + 1).trim();
                    if (key && value) {
                        newCookies.push({
                            id: uuid(),
                            key: key,
                            value: value
                        });
                    }
                }
            }

            console.log(newCookies)

            if (newCookies.length > 0) {
                setCookies([...newCookies]);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Invalid cookie string format:', error);
            return false;
        }
    }

    const handlePasteHeaders = (e) => {
        const pastedText = e.clipboardData.getData('text').trim();

        // Check if it looks like JSON and is a valid JSON object
        if (pastedText.startsWith('{') && pastedText.endsWith('}')) {
            try {
                const parsed = JSON.parse(pastedText);
                // Only proceed if it's a valid object with at least one key-value pair
                if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) && Object.keys(parsed).length > 0) {
                    e.preventDefault();
                    const success = handleParseJsonToHeaders(pastedText);
                    if (success) {
                        // Clear the input field after successful JSON parse
                        e.target.value = '';
                    }
                    return;
                }
            } catch (error) {
                // Not valid JSON, continue checking other formats
            }
        }

        // Check if it looks like header string format (key: value; key: value or key:value\nkey:value)
        console.log("pastedText", pastedText)
        if (pastedText.includes('=') && pastedText.includes(';')) {
            console.log("chay den day")
            e.preventDefault();
            const success = handleParseHeaderString(pastedText);
            if (success) {
                // Clear the input field after successful header string parse
                e.target.value = '';
            }
            return;
        }

        // For normal text or unrecognized format, let default paste behavior happen
    }

    // handle paste event for cookies
    const handlePasteCookies = (e) => {
        const pastedText = e.clipboardData.getData('text').trim();

        // Check if it looks like JSON and is a valid JSON object
        if (pastedText.startsWith('{') && pastedText.endsWith('}')) {
            try {
                const parsed = JSON.parse(pastedText);
                // Only proceed if it's a valid object with at least one key-value pair
                if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) && Object.keys(parsed).length > 0) {
                    e.preventDefault();
                    const success = handleParseJsonToCookies(pastedText);
                    if (success) {
                        // Clear the input field after successful JSON parse
                        e.target.value = '';
                    }
                    return;
                }
            } catch (error) {
                // Not valid JSON, continue checking other formats
            }
        }

        // Check if it looks like cookie string format (key=value;key=value;...)
        if (pastedText.includes('=') && pastedText.includes(';')) {
            e.preventDefault();
            const success = handleParseCookieString(pastedText);
            if (success) {
                // Clear the input field after successful cookie string parse
                e.target.value = '';
            }
            return;
        }

        // For normal text or unrecognized format, let default paste behavior happen
    }

    // auto-detect format and parse
    const detectAndParseFormat = (inputText) => {
        const trimmedText = inputText.trim();

        // Detect cURL commands
        if (trimmedText.startsWith('curl')) {
            // Check for Windows format (contains ^ characters)
            if (trimmedText.includes('^')) {
                return parseCurlCommand(trimmedText);
            } else {
                // Bash/Linux format
                return parseCurlBashCommand(trimmedText);
            }
        }

        // Detect fetch API
        if (trimmedText.includes('fetch(')) {
            return parseFetchCode(trimmedText);
        }

        throw new Error('Unsupported format. Please paste a cURL command or JavaScript fetch code.');
    }

    // handle actual import after text input
    const processImport = () => {
        if (!importText.trim()) return;

        try {
            const parsed = detectAndParseFormat(importText.trim());

            // Reset all fields to default values first
            setUrl(parsed.url || "");
            setMethod(parsed.method || "GET");
            setHeaders(parsed.headers || []);
            setCookies(parsed.cookies || []);
            
            // Convert body to appropriate format based on content type
            let bodyData = parsed.body || "";
            const parsedContentType = parsed.contentType || "";
            
            // Set content type FIRST (important for FormDataBody to know the type)
            setContentType(parsedContentType);
            
            // If content type is form-data or url-encoded and body is a string, convert to array
            if ((parsedContentType === "application/x-www-form-urlencoded" || 
                 parsedContentType === "multipart/form-data") && 
                typeof bodyData === 'string' && bodyData.trim()) {
                // Parse URL-encoded string into array of form fields
                const formFields = [];
                const pairs = bodyData.split('&');
                for (const pair of pairs) {
                    const equalIndex = pair.indexOf('=');
                    if (equalIndex > 0) {
                        const name = pair.substring(0, equalIndex);
                        const value = pair.substring(equalIndex + 1);
                        // Safely decode URI components, fallback to original if decode fails
                        let decodedName = name;
                        let decodedValue = value;
                        try {
                            decodedName = decodeURIComponent(name);
                            decodedValue = decodeURIComponent(value);
                        } catch (e) {
                            console.warn('Failed to decode URI component:', e);
                        }
                        formFields.push({
                            id: uuid(),
                            name: decodedName,
                            value: decodedValue,
                            type: "text"
                        });
                    }
                }
                // If no fields -> keep empty (no placeholder row)
                bodyData = formFields;
                console.log('Converted body to form fields:', formFields);
            }
            
            // Set body data in next tick to ensure contentType state is updated first
            setTimeout(() => {
                setDataString(bodyData);
                console.log('Import completed - ContentType:', parsedContentType, 'BodyData:', bodyData);
            }, 0);


            // Reset import state
            setImportText('');
            setImportDropdownOpen(false); // Close the dropdown

            console.log('Successfully imported request:', parsed);
        } catch (error) {
            alert('Failed to parse the input. Please check the format and try again.\n\nError: ' + error.message);
            console.error('Import error:', error);
        }
    }

    // cancel import
    const cancelImport = () => {
        setImportText('');
        setImportDropdownOpen(false); // Close the dropdown when cancelling
    }

    // parse cURL command
    const parseCurlCommand = (curlCommand) => {
        const result = {
            method: 'GET',
            url: '',
            headers: [],
            cookies: [],
            body: '',
            contentType: ''
        };

        console.log('Parsing cURL command:', curlCommand);

        // Clean up Windows format (remove ^ line continuations and normalize)
        let cleanCommand = curlCommand
            .replace(/\s*\^\s*\n/g, ' ')  // Remove ^ line continuations
            .replace(/\s*\^\s*$/gm, ' ')  // Remove ^ at end of lines
            .replace(/\s+/g, ' ')         // Normalize whitespace
            .trim();

        // Clean up Windows escape patterns globally
        cleanCommand = cleanCommand
            .replace(/\^\\\^"/g, '"')     // ^\^" -> " (nested escaped quotes)
            .replace(/\^%\^2F/g, '/')     // ^%^2F -> / (URL encoded slash)
            .replace(/\^%\^7C/g, '|')     // ^%^7C -> | (URL encoded pipe)
            .replace(/\^\$o/g, '.')       // ^$o -> . (special Windows encoding)
            .replace(/\^\$g/g, '.')       // ^$g -> . (special Windows encoding)
            .replace(/\^\$t/g, '.')       // ^$t -> . (special Windows encoding)
            .replace(/\^\$j/g, '.')       // ^$j -> . (special Windows encoding)
            .replace(/\^\$l/g, '.')       // ^$l -> . (special Windows encoding)
            .replace(/\^\$h/g, '.')       // ^$h -> . (special Windows encoding)
            .replace(/\^%/g, '%');        // ^% -> % (for URL encoding)

        console.log('Cleaned command:', cleanCommand);

        // Extract URL - handle various quote formats
        const urlMatch = cleanCommand.match(/curl\s+(?:-[^\s]+\s+)*(?:\^?"([^"]+)"\^?|([^\s]+))/);
        if (urlMatch) {
            result.url = urlMatch[1] || urlMatch[2];
            // Clean up Windows-style quotes
            result.url = result.url.replace(/\^"/g, '"').replace(/"\^/g, '"');
        }
        console.log('Extracted URL:', result.url);

        // Extract method
        const methodMatch = cleanCommand.match(/-X\s+(?:\^?"([^"\s]+)"\^?|([^\s]+))/i);
        console.log("methodMatch:", methodMatch)
        if (methodMatch) {
            console.log("222")
            result.method = (methodMatch[1] || methodMatch[2]).toUpperCase();
        } else {
            // Check for data flags that imply POST (similar to Bash parser)
            const hasDataFlag = /(?:--data-raw|--data-urlencode|--data-binary|--data|--form|-d|-F)\s+/i.test(cleanCommand);
            if (hasDataFlag) {
                result.method = 'POST'; 
            } else {
                result.method = 'GET';
            }
        }

        console.log('Extracted method:', result.method);

        // Extract headers - improved Windows format handling with better escape support
        const headerRegex = /(?:-H|--header)\s+(\^?")((?:[^"]|\^.|(?!\1)")*?)\1\^?/g;
        let headerMatch;
        while ((headerMatch = headerRegex.exec(cleanCommand)) !== null) {
            let headerString = headerMatch[2];

            // Clean up Windows escaping - improved handling for all escape sequences
            headerString = headerString
                .replace(/\^\\\^"/g, '"')   // ^\^" -> " (nested escaped quotes)
                .replace(/\^%\^2F/g, '/')   // ^%^2F -> / (URL encoded slash)
                .replace(/\^%\^7C/g, '|')   // ^%^7C -> | (URL encoded pipe)
                .replace(/\^%/g, '%')       // ^% -> % (for URL encoding)
                .replace(/\^\^"/g, '"')     // ^^" -> "
                .replace(/"\^\^/g, '"')     // "^^ -> "
                .replace(/\^"/g, '"')       // ^" -> "
                .replace(/"\^/g, '"')       // "^ -> "
                .replace(/\^\^/g, '^')      // ^^ -> ^
                .replace(/\\"/g, '"');      // \" -> " (additional escape handling)

            console.log('Processing header:', headerString);

            const colonIndex = headerString.indexOf(':');
            if (colonIndex > 0) {
                const key = headerString.substring(0, colonIndex).trim();
                const value = headerString.substring(colonIndex + 1).trim();
                result.headers.push({
                    id: uuid(),
                    key: key,
                    value: value
                });

                // Check for content-type
                if (key.toLowerCase() === 'content-type') {
                    result.contentType = value;
                }
            }
        }
        console.log('Extracted headers:', result.headers);

        // Extract cookies - check if any header contains cookie and separate them
        const cookieHeaders = result.headers.filter(header => header.key.toLowerCase() === 'cookie');
        cookieHeaders.forEach(header => {
            console.log('Found cookie header from parsed headers:', header.value)
            let cookieString = header.value;

            // Additional Windows escaping cleanup for cookies
            cookieString = cookieString
                .replace(/\^%/g, '%')           // ^% -> %
                .replace(/\^"/g, '"')           // ^" -> "
                .replace(/"\^/g, '"')           // "^ -> "
                .replace(/\^\^/g, '^');         // ^^ -> ^

            console.log('Cookie string after Windows cleanup:', cookieString);

            const cookiePairs = cookieString.split(';');
            for (const pair of cookiePairs) {
                const equalIndex = pair.indexOf('=');
                if (equalIndex > 0) {
                    const key = pair.substring(0, equalIndex).trim();
                    const value = pair.substring(equalIndex + 1).trim();
                    result.cookies.push({
                        id: uuid(),
                        key: key,
                        value: value
                    });
                }
            }
        });

        // Remove cookie headers from headers array
        result.headers = result.headers.filter(header => header.key.toLowerCase() !== 'cookie');
        console.log('Extracted cookies:', result.cookies);

        // Check if --form exists in cleanCommand, set contentType first
        if (/(?:--form|-F)\s+/i.test(cleanCommand)) {
            result.contentType = 'multipart/form-data';
            console.log('Detected --form, setting contentType to multipart/form-data');
        }

        // Extract body data - handle multiple formats
        // First check for --form or -F (multipart/form-data)
        // Updated regex to be more flexible with quotes
        const formRegex = /(?:--form|-F)\s+(?:\^?['"]([^'"]*)["']\^?|\^?"([^"\s]+)"\^?|([^\s]+))/g;
        let formMatch;
        const formParts = [];
        console.log('Checking for --form in cleanCommand:', cleanCommand);
        while ((formMatch = formRegex.exec(cleanCommand)) !== null) {
            console.log('formMatch found:', formMatch);
            let part = (formMatch[1] || formMatch[2] || formMatch[3] || '').replace(/\^/g, '');
            if (!part) continue;
            
            // Remove outer quotes if present
            part = part.replace(/^['"]|['"]$/g, '');
            // Remove quotes around values like a="b" -> a=b, but keep file paths like file=@path.txt
            if (!part.includes('=@')) {
                part = part.replace(/=["']([^"']+)["']$/, '=$1');
            }
            console.log('Extracted form part:', part);
            formParts.push(part);
        }
        
        if (formParts.length > 0) {
            result.body = formParts.join('&');
            console.log('Extracted form-data body:', result.body);
        } else {
            console.log('No form parts found');
            // Check for --data-urlencode (can have multiple instances)
            const urlEncodeRegex = /--data-urlencode\s+(?:\^?['"]([^'"]+)['"]\^?|([^\s]+))/g;
            let urlEncodeMatch;
            const urlEncodedParts = [];
            while ((urlEncodeMatch = urlEncodeRegex.exec(cleanCommand)) !== null) {
                const part = (urlEncodeMatch[1] || urlEncodeMatch[2]).replace(/\^/g, '');
                urlEncodedParts.push(part);
            }
            
            if (urlEncodedParts.length > 0) {
                result.body = urlEncodedParts.join('&');
                console.log('Extracted URL-encoded body:', result.body);
            } else {
                // Fallback to regular --data, --data-raw, or -d
                const bodyMatch = cleanCommand.match(/(?:-d|--data|--data-raw|--data-binary)\s+(?:\^"(.+?)"\^?|([^\s]+))/);
                console.log('cleanCommand:', cleanCommand);
                console.log('Body match:', bodyMatch);
                if (bodyMatch) {
                    result.body = (bodyMatch[1] || bodyMatch[2]).replace(/\^/g, '');
                    console.log('Extracted body:', result.body);
                }
            }
        }
        console.log('Final body:', result.body);

        return result;
    }

    // parse cURL Bash command
    const parseCurlBashCommand = (curlCommand) => {
        const result = {
            method: 'GET',
            url: '',
            headers: [],
            cookies: [],
            body: '',
            contentType: ''
        };

        console.log('Parsing cURL Bash command:', curlCommand);

        // Clean up Bash format (remove \ line continuations and normalize)
        let cleanCommand = curlCommand
            .replace(/\s*\\\s*\n/g, ' ')  // Remove \ line continuations
            .replace(/\s*\\\s*$/gm, ' ')  // Remove \ at end of lines
            .replace(/\s+/g, ' ')         // Normalize whitespace
            .trim();

        console.log('Cleaned Bash command:', cleanCommand);

        // Extract URL - handle various quote formats and multiple flags
        // Try different patterns to find URL
        let urlMatch = cleanCommand.match(/['"]https?:\/\/[^'"]+['"]/); // First try quoted URLs
        if (!urlMatch) {
            urlMatch = cleanCommand.match(/https?:\/\/[^\s]+/); // Then try unquoted URLs
        }
        if (!urlMatch) {
            // Fallback to original pattern
            urlMatch = cleanCommand.match(/curl\s+(?:(?:--?\w+(?:[-\w]*)*(?:\s+(?:['"][^'"]*['"]|[^\s-][^\s]*))?\s+)*)(?:['"]([^'"]+)['"]|([^\s-][^\s]*))/);
        }

        if (urlMatch) {
            result.url = urlMatch[1] || urlMatch[2] || urlMatch[0];
            // Clean quotes if present
            result.url = result.url.replace(/^['"]|['"]$/g, '');
        }
        console.log('Extracted URL:', result.url);

        // Extract method - handle both -X and --request
        const methodMatch = cleanCommand.match(/-X\s+(?:['"]([^'"]+)['"]|([^\s]+))/i) ||
            cleanCommand.match(/--request\s+(?:['"]([^'"]+)['"]|([^\s]+))/i);
        if (methodMatch) {
            result.method = (methodMatch[1] || methodMatch[2] || methodMatch[3] || methodMatch[4]).toUpperCase();
            console.log('Found explicit method:', result.method);
        } else {
            // Check for data flags that imply POST
            const hasDataFlag = cleanCommand.match(/(?:--data-raw|--data-urlencode|--data-binary|--data|--form|-d|-F)\s+/i);
            if (hasDataFlag) {
                result.method = 'POST';
                console.log('Found data flag, setting method to POST');
            }
            // Otherwise, method remains 'GET' (default set above)
        }
        console.log('Extracted method:', result.method);

        // Extract headers - improved to handle nested quotes and both -H and --header
        const headerRegex = /(?:-H|--header)\s+(['"])((?:[^'"]|\\['"]|(?!\1)['"])*?)\1/g;
        let headerMatch;
        while ((headerMatch = headerRegex.exec(cleanCommand)) !== null) {
            let headerString = headerMatch[2];

            // Handle escaped quotes
            headerString = headerString
                .replace(/\\'/g, "'")
                .replace(/\\"/g, '"');

            console.log('Processing header:', headerString);

            const colonIndex = headerString.indexOf(':');
            if (colonIndex > 0) {
                const key = headerString.substring(0, colonIndex).trim();
                const value = headerString.substring(colonIndex + 1).trim();
                result.headers.push({
                    id: uuid(),
                    key: key,
                    value: value
                });

                // Check for content-type
                if (key.toLowerCase() === 'content-type') {
                    result.contentType = value;
                }
            }
        }
        console.log('Extracted headers:', result.headers);

        // Extract cookies - check if any header contains cookie and separate them  
        const cookieHeaders = result.headers.filter(header => header.key.toLowerCase() === 'cookie');
        cookieHeaders.forEach(header => {
            console.log('Found cookie header from parsed headers:', header.value)
            const cookieString = header.value;
            const cookiePairs = cookieString.split(';');
            for (const pair of cookiePairs) {
                const equalIndex = pair.indexOf('=');
                if (equalIndex > 0) {
                    const key = pair.substring(0, equalIndex).trim();
                    const value = pair.substring(equalIndex + 1).trim();
                    result.cookies.push({
                        id: uuid(),
                        key: key,
                        value: value
                    });
                }
            }
        });

        // Remove cookie headers from headers array
        result.headers = result.headers.filter(header => header.key.toLowerCase() !== 'cookie');
        console.log('Extracted cookies:', result.cookies);

        // Check if --form exists in Cleaned Bash command, set contentType first
        if (/(?:--form|-F)\s+/i.test(cleanCommand)) {
            result.contentType = 'multipart/form-data';
            console.log('Detected --form in Bash command, setting contentType to multipart/form-data');
        }

        // Extract body data - handle multiple formats
        // First check for --form or -F (multipart/form-data)
        // Match patterns like: --form 'a="b"' or --form "a=b" or --form a=b
        const formRegex = /(?:--form|-F)\s+(?:'([^']*)'|"([^"]*)"|([^\s]+))/g;
        let formMatch;
        const formParts = [];
        console.log('Checking for --form in Bash cleanCommand:', cleanCommand);
        while ((formMatch = formRegex.exec(cleanCommand)) !== null) {
            console.log('Bash formMatch found:', formMatch);
            let part = formMatch[1] || formMatch[2] || formMatch[3] || '';
            if (!part) continue;
            
            // Remove quotes around values like a="b" -> a=b, but keep file paths like file=@path.txt
            if (!part.includes('=@')) {
                part = part.replace(/=["']([^"']+)["']$/, '=$1');
            }
            console.log('Extracted Bash form part:', part);
            formParts.push(part);
        }
        
        if (formParts.length > 0) {
            result.body = formParts.join('&');
            console.log('Extracted form-data body from Bash command:', result.body);
        } else {
            console.log('No Bash form parts found');
            // Check for --data-urlencode (can have multiple instances)
            const urlEncodeRegex = /--data-urlencode\s+(['"])([^'"]+)\1/g;
            let urlEncodeMatch;
            const urlEncodedParts = [];
            while ((urlEncodeMatch = urlEncodeRegex.exec(cleanCommand)) !== null) {
                urlEncodedParts.push(urlEncodeMatch[2]);
            }
            
            if (urlEncodedParts.length > 0) {
                result.body = urlEncodedParts.join('&');
                console.log('Extracted URL-encoded body:', result.body);
            } else {
                // Try to find multiline data in original command (preserves comments)
                const multilineBodyMatch = curlCommand.match(/(?:-d|--data|--data-raw|--data-binary)\s+(['"])([\s\S]*?)\1(?=\s*\\\s*$|\s*\\\s*-|\s*$)/);

                if (multilineBodyMatch) {
                    result.body = multilineBodyMatch[2];
                    console.log('Found multiline body with comments preserved');
                } else {
                    // Fallback to single line parsing from cleaned command
                    const bodyMatch = cleanCommand.match(/(?:-d|--data|--data-raw|--data-binary)\s+(['"])([^'"]*?)\1/) ||
                        cleanCommand.match(/(?:-d|--data|--data-raw|--data-binary)\s+\$'([^']*)'/) ||
                        cleanCommand.match(/(?:-d|--data|--data-raw|--data-binary)\s+([^\s]+)/);
                    if (bodyMatch) {
                        result.body = bodyMatch[2] || bodyMatch[3] || bodyMatch[4];
                        // Handle escaped characters in $'...' format
                        if (bodyMatch[3] && curlCommand.includes("$'")) {
                            result.body = result.body
                                .replace(/\\n/g, '\n')
                                .replace(/\\t/g, '\t')
                                .replace(/\\r/g, '\r')
                                .replace(/\\'/g, "'")
                                .replace(/\\\\/g, '\\');
                        }
                    }
                }
            }
        }
        console.log('Extracted body:', result.body);

        return result;
    }

    // parse JavaScript/Node.js fetch code
    const parseFetchCode = (fetchCode) => {
        const result = {
            method: 'GET',
            url: '',
            headers: [],
            cookies: [],
            body: '',
            contentType: ''
        };

        console.log('Parsing fetch code:', fetchCode);

        // Extract URL
        const urlMatch = fetchCode.match(/fetch\s*\(\s*['"`]([^'"`]+)['"`]/);
        if (urlMatch) {
            result.url = urlMatch[1];
        }
        console.log('Extracted URL:', result.url);

        // Extract method - handle both quoted and unquoted keys, and requestOptions pattern
        let methodMatch = fetchCode.match(/(?:['"`])?method(?:['"`])?\s*:\s*['"`](\w+)['"`]/i);
        if (!methodMatch) {
            // Try to find method in requestOptions or similar variable
            methodMatch = fetchCode.match(/var\s+\w+\s*=\s*\{[\s\S]*?(?:['"`])?method(?:['"`])?\s*:\s*['"`](\w+)['"`]/i);
        }
        if (methodMatch) {
            result.method = methodMatch[1].toUpperCase();
        }
        console.log('Extracted method:', result.method);

        // Extract headers - handle both object literal and Headers constructor patterns
        let headersProcessed = false;

        // First try Headers constructor pattern (new Headers() with append calls)
        const headersConstructorMatch = fetchCode.match(/var\s+(\w+)\s*=\s*new\s+Headers\(\)/i);
        if (headersConstructorMatch) {
            const headersVarName = headersConstructorMatch[1];
            console.log('Found Headers constructor with variable:', headersVarName);

            // Find all append calls for this headers variable - handle escaped quotes in values
            const appendRegex = new RegExp(`${headersVarName}\\.append\\s*\\(\\s*['"]([^'"]+)['"]\\s*,\\s*['"]((\\\\.|[^'"\\\\])*?)['"]\\s*\\)`, 'gi');
            let appendMatch;
            while ((appendMatch = appendRegex.exec(fetchCode)) !== null) {
                const key = appendMatch[1];
                let value = appendMatch[2];
                
                // Handle escaped quotes in value
                value = value.replace(/\\"/g, '"').replace(/\\'/g, "'");
                
                console.log('Found header append:', key, '=', value);
                
                result.headers.push({
                    id: uuid(),
                    key: key,
                    value: value
                });

                // Check for content-type
                if (key.toLowerCase() === 'content-type') {
                    result.contentType = value;
                }

                // Handle Cookie header - extract individual cookies
                if (key.toLowerCase() === 'cookie') {
                    console.log('Found cookie header:', value);
                    const cookiePairs = value.split(';');
                    for (const pair of cookiePairs) {
                        const equalIndex = pair.indexOf('=');
                        if (equalIndex > 0) {
                            const cookieKey = pair.substring(0, equalIndex).trim();
                            const cookieValue = pair.substring(equalIndex + 1).trim();
                            result.cookies.push({
                                id: uuid(),
                                key: cookieKey,
                                value: cookieValue
                            });
                        }
                    }
                }
            }

            // Remove cookie headers from headers array since we've extracted them separately
            result.headers = result.headers.filter(header => header.key.toLowerCase() !== 'cookie');
            headersProcessed = true;
            console.log('Processed Headers constructor pattern');
        }

        // If no Headers constructor found, try object literal pattern
        if (!headersProcessed) {
            let headersMatch = fetchCode.match(/['"`]headers['"`]\s*:\s*(\{[\s\S]*?\})/);
            if (!headersMatch) {
                // Try without quotes around headers key
                headersMatch = fetchCode.match(/headers\s*:\s*(\{[\s\S]*?\})/);
            }

            if (headersMatch) {
                try {
                    let headersString = headersMatch[1];
                    console.log('Raw headers string:', headersString);

                    // More sophisticated cleaning for headers object
                    // Handle multi-line formatting
                    headersString = headersString
                        .replace(/,\s*\}/g, '}')  // Remove trailing commas
                        .replace(/(['"`])([^'"`:,\}]+)\1\s*:/g, '"$2":')  // Normalize keys
                        .replace(/:\s*(['"`])([^'"`,\}]*)\1/g, ':"$2"');  // Normalize values

                    console.log('Cleaned headers string:', headersString);

                    const headersObj = JSON.parse(headersString);
                    console.log('Parsed headers object:', headersObj);

                    Object.entries(headersObj).forEach(([key, value]) => {
                        result.headers.push({
                            id: uuid(),
                            key: key,
                            value: String(value)
                        });

                        // Check for content-type
                        if (key.toLowerCase() === 'content-type') {
                            result.contentType = String(value);
                        }
                    });
                } catch (error) {
                    console.warn('Failed to parse headers object:', error);
                    console.warn('Headers string was:', headersMatch[1]);
                }
            }
        }
        console.log('Extracted headers:', result.headers);

        // Extract body - handle null, string, JSON.stringify, and variable references
        console.log('Input fetchCode:', fetchCode);
        console.log('Code length:', fetchCode.length);

        // First check for variable reference in body (including requestOptions pattern)
        let bodyVariableMatch = fetchCode.match(/(?:['"`])?body(?:['"`])?\s*:\s*(\w+)(?:\s*,|\s*\})/);
        if (!bodyVariableMatch) {
            // Try to find body in requestOptions or similar variable
            bodyVariableMatch = fetchCode.match(/var\s+\w+\s*=\s*\{[\s\S]*?(?:['"`])?body(?:['"`])?\s*:\s*(\w+)(?:\s*,|\s*\})/);
        }
        
        if (bodyVariableMatch && bodyVariableMatch[1] !== 'null') {
            const variableName = bodyVariableMatch[1];
            console.log('Found body variable reference:', variableName);

            // First try to find JSON.stringify pattern
            const jsonStringifyRegex = new RegExp(`var\\s+${variableName}\\s*=\\s*JSON\\.stringify\\s*\\([\\s\\S]*?\\)\\s*;?`, 'i');
            const jsonStringifyMatch = fetchCode.match(jsonStringifyRegex);
            
            if (jsonStringifyMatch) {
                console.log('Found JSON.stringify pattern:', jsonStringifyMatch[0]);
                
                // Extract the content inside JSON.stringify()
                const stringifyContentMatch = jsonStringifyMatch[0].match(/JSON\.stringify\s*\(([\s\S]*?)\)\s*;?\s*$/i);
                if (stringifyContentMatch) {
                    let jsonContent = stringifyContentMatch[1].trim();
                    console.log('Raw JSON content from stringify:', jsonContent);
                    
                    // Try to parse and re-stringify to clean up formatting
                    try {
                        const parsed = JSON.parse(jsonContent);
                        result.body = JSON.stringify(parsed);
                        console.log('Cleaned JSON body from JSON.stringify:', result.body);
                    } catch (e) {
                        console.log('Failed to parse JSON from stringify, using raw content');
                        result.body = jsonContent;
                    }
                } else {
                    console.log('Could not extract content from JSON.stringify');
                }
            } else {
                // Fallback to original string variable pattern
                const variableRegex = new RegExp(`var\\s+${variableName}\\s*=\\s*(['"])((?:\\\\.|(?!\\1)[\\s\\S])*?)\\1\\s*;?`, 'i');
                const variableMatch = fetchCode.match(variableRegex);
                if (variableMatch) {
                    console.log('Raw extracted body from variable:', variableMatch[2]);
                    result.body = variableMatch[2]
                        .replace(/\\r\\n/g, '\n')  // Handle \r\n
                        .replace(/\\n/g, '\n')     // Handle \n
                        .replace(/\\t/g, '\t')     // Handle \t
                        .replace(/\\"/g, '"')      // Handle \"
                        .replace(/\\\\/g, '\\');   // Handle \\
                    console.log('Final processed body:', result.body);
                } else {
                    console.log('Could not find variable declaration for:', variableName);
                }
            }
        } else {
            // Fallback to original patterns
            const bodyMatch =
                fetchCode.match(/['"`]body['"`]\s*:\s*null/i) ||
                fetchCode.match(/body\s*:\s*null/i) ||
                fetchCode.match(/['"`]body['"`]\s*:\s*JSON\.stringify\s*\(\s*([^)]+)\s*\)/) ||
                fetchCode.match(/body\s*:\s*JSON\.stringify\s*\(\s*([^)]+)\s*\)/) ||
                fetchCode.match(/['"`]body['"`]\s*:\s*(['"`])((?:\\.|(?!\1).)*?)\1/s);

            console.log('Body match:', bodyMatch);

            if (bodyMatch) {
                if (bodyMatch[0].includes('null')) {
                    result.body = '';  // null body means empty
                } else if (bodyMatch[0].includes('JSON.stringify')) {
                    // For JSON.stringify patterns, the content is in capture group 1
                    result.body = bodyMatch[1];
                } else {
                    // For quoted string patterns, the content is in capture group 2
                    // (group 1 is the quote character, group 2 is the content)
                    const bodyString = bodyMatch[2];
                    result.body = bodyString.replace(/\\(.)/g, '$1');
                }
            }
        }
        console.log('Extracted body:', result.body);

        return result;
    }

    // handle change input headers
    const handleChangeHeaders = (id) => (e) => {
        const { name } = e.target
        const value = e.newValue || e.target.value
        let result = headers.map((item) => {
            if (item.id === id) {
                return { ...item, [name]: value }
            } else {
                return item
            }
        })
        setHeaders(result)
    }
    // handle change input cookies
    const handleChangeCookies = (id) => (e) => {
        const { name } = e.target
        const value = e.newValue || e.target.value
        let result = cookies.map((item) => {
            if (item.id === id) {
                return { ...item, [name]: value }
            } else {
                return item
            }
        })
        setCookies(result)
    }

    // handle change input key body variable
    const handleChangeBodyVariables = (id) => (e) => {
        const { name, value } = e.target
        let result = outputBodyVariables.map((item) => {
            if (item.id === id) {
                return { ...item, [name]: value }
            } else {
                return item
            }
        })
        setOutputBodyVariables(result)
    }

    // handle change select value body variable
    const handleSelectBodyVariable = (value, id) => {
        let result = outputBodyVariables.map((item) => {
            if (item.id === id) {
                return { ...item, "value": value }
            } else {
                return item
            }
        })
        setOutputBodyVariables(result)
    }

    // handle change input key header variable
    const handleChangeHeaderVariable = (id) => (e) => {
        const { name, value } = e.target
        let result = outputHeadersVariables.map((item) => {
            if (item.id === id) {
                return { ...item, [name]: value }
            } else {
                return item
            }
        })
        setOutputHeadersVariables(result)
    }

    // handle change select value header variable
    const handleSelectHeaderVariable = (value, id) => {
        let result = outputHeadersVariables.map((item) => {
            if (item.id === id) {
                return { ...item, "value": value }
            } else {
                return item
            }
        })
        setOutputHeadersVariables(result)
    }

    // handle change input key cookie variable
    const handleChangeCookieVariable = (id) => (e) => {
        const { name, value } = e.target
        let result = outputCookiesVariables.map((item) => {
            if (item.id === id) {
                return { ...item, [name]: value }
            } else {
                return item
            }
        })
        setOutputCookiesVariables(result)
    }

    // handle change select value cookie variable
    const handleSelectCookieVariable = (value, id) => {
        let result = outputCookiesVariables.map((item) => {
            if (item.id === id) {
                return { ...item, "value": value }
            } else {
                return item
            }
        })
        setOutputCookiesVariables(result)
    }


    // function to remove comments from body text before sending to backend
    const removeCommentsFromBody = (bodyText) => {
        console.log('=== REMOVING COMMENTS FROM BODY ===');
        console.log('Original body text (with comments):', bodyText);
        
        if (!bodyText || typeof bodyText !== 'string') {
            return bodyText;
        }
        
        let result = bodyText;
        
        // Remove block comments (/* ... */) first
        result = result.replace(/\/\*[\s\S]*?\*\//g, '');
        
        // Remove single-line comments (// ...) - both at start of line and inline
        // This regex handles both cases: full line comments and inline comments
        result = result.replace(/\/\/.*$/gm, '');
        
        // Remove hash comments (# ...) - both at start of line and inline
        result = result.replace(/#.*$/gm, '');
        
        // Remove trailing commas that might be left before removed comments
        result = result.replace(/,(\s*[\r\n]+\s*[}\]])/g, '$1');
        
        // Remove empty lines that might be left after comment removal
        result = result.replace(/^\s*[\r\n]/gm, '');
        
        // Clean up any trailing whitespace on lines
        result = result.replace(/[ \t]+$/gm, '');
        
        // Trim leading/trailing whitespace
        result = result.trim();
        
        console.log('Body text after comment removal:', result);
        return result;
    };

    // save node data
    const handleSetDataBaseModal = props.handleSetDataBaseModal;
    useEffect(() => {
        console.log('=== SAVING NODE DATA ===');
        console.log('Original dataString (with comments):', dataString);

        // Always remove comments when saving, but keep original in UI
        const cleanedDataString = removeCommentsFromBody(dataString);

        console.log('Final dataString to save (comments removed):', cleanedDataString);
        console.log('Data changed:', dataString !== cleanedDataString);

        const options = {
            contentType: contentType,
            url: url,
            method: method,
            headers: headers,
            cookies: cookies,
            dataString: dataString,
            responseMode: responseMode,
            simpleResponseVariable: simpleResponseVariable,
            dataString: cleanedDataString,
            outputBodyVariables: outputBodyVariables,
            outputHeadersVariables: outputHeadersVariables,
            outputCookiesVariables: outputCookiesVariables
        }

        console.log('Complete options object:', options);
        handleSetDataBaseModal(options)
    }, [handleSetDataBaseModal, url, headers, cookies, contentType, method, dataString, responseMode, simpleResponseVariable, outputBodyVariables, outputHeadersVariables, outputCookiesVariables])

    const newData = data.nodeValue.variables.filter(variable => variable.label !== "")

    const theme = useMantineTheme();
    const isTet = theme.colorScheme === 'tet';

    const leftSection = (<ActionIcon radius="md"><IconCode size={16} /></ActionIcon>)
    const rightSection = (<ActionIcon radius="md" onClick={handleDropdown} variant="gradient"
        gradient={
            isTet
                ? { from: '#d32f2f', to: '#fbc02d', deg: 90 }
                : { from: '#f08c00', to: '#f08c00', deg: 90 }
        }><IconCode size={16} /></ActionIcon>)

    const onChangeBody = e => {
        console.log('=== BODY TEXT CHANGED ===');
        console.log('Event type:', e?.type);

        if (e?.type === 'change') {
            console.log('Change event - new value:', e.target.value);
            setDataString(e.target.value)
        } else if (e?.type === 'click') {
            const textarea = textareaRefBody.current;
            const startPos = textarea.selectionStart;
            const firstPart = dataString.slice(0, startPos);
            const secondPart = dataString.slice(startPos);
            const value = e.target.getAttribute('value')
            const newValue = firstPart + "${" + value + "}" + secondPart
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(startPos + value.length + 4, startPos + value.length + 3);
            }, 0);
            setOpenDropdown(false)
            setDataString(newValue)
            console.log('Click event - adding variable');
            console.log('Adding variable:', value, '-> Result:', newValue);
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(startPos + value.length + 4, startPos + value.length + 3);
            }, 0);
            setOpenDropdown(false)
            setDataString(newValue)
        } else {
            console.log('Direct value change:', e);
            setDataString(e)
        }
    }

    return (
        <>
            <Popover width={420} position="bottom" shadow="xl" opened={importDropdownOpen} onChange={setImportDropdownOpen}>
                <Popover.Target>
                    <Button
                        mt="sm"
                        variant="gradient"
                        gradient={
                            isTet
                                ? { from: '#d32f2f', to: '#fbc02d', deg: 90 }
                                : { from: 'blue', to: 'cyan', deg: 45 }
                        }
                        fullWidth
                        leftIcon={<IconDownload size={16} />}
                        onClick={() => setImportDropdownOpen(true)}
                        styles={{
                            root: {
                                height: '42px',
                                borderRadius: '8px',
                                fontWeight: 600,
                            }
                        }}
                    >
                        Import Request
                    </Button>
                </Popover.Target>
                <Popover.Dropdown
                    style={{
                        padding: '16px',
                        borderRadius: '12px',
                        border: '1px solid #e9ecef'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                        <Text size="sm" fw={600} color="dark" style={{ flex: 1 }}>
                            Paste your cURL command or JavaScript fetch code:
                        </Text>
                    </div>

                    <Text size="xs" color="dimmed" mb="sm">
                        Supports: cURL (Windows/Linux), JavaScript fetch, Node.js fetch
                    </Text>

                    <Textarea
                        placeholder='Examples:
                            curl "https://api.example.com" -H "Content-Type: application/json"
                            curl ^"https://api.example.com^" -H ^"Content-Type: application/json^"
                            fetch("https://api.example.com", { method: "POST", headers: {...} })'
                        value={importText}
                        onChange={(e) => setImportText(e.currentTarget.value)}
                        minRows={8}
                        maxRows={12}
                        style={{
                            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                            fontSize: '12px'
                        }}
                    />

                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <Button
                            onClick={processImport}
                            disabled={!importText.trim()}
                            variant="gradient"
                            gradient={{ from: 'green', to: 'lime', deg: 45 }}
                            style={{ flex: 1 }}
                        >
                            Import
                        </Button>
                        <Button
                            onClick={cancelImport}
                            variant="light"
                            color="gray"
                            style={{ flex: 1 }}
                        >
                            Cancel
                        </Button>
                    </div>
                </Popover.Dropdown>
            </Popover>
            <Popover opened={openDropdown} width="target" position="bottom" shadow="md">
                <Popover.Target>
                    <TextInput
                        ref={textareaRef}
                        placeholder="URL"
                        value={url}
                        label="Enter URL"
                        onChange={(event) => handleInputValue(event.currentTarget.value)}
                        rightSection={rightSection}
                    />
                </Popover.Target>
                <Popover.Dropdown>
                    <div style={{ maxHeight: '220px', display: 'flex' }}>
                        <ScrollArea style={{ flex: '1' }}>
                            <div className={styles.select_itemsWrapper} style={{ flexDirection: 'column' }}>
                                {data.nodeValue.variables.map((ele, index) => {
                                    return (
                                        <li
                                            key={index}
                                            onClick={(e) => handleSetValue(e)}
                                            value={ele.value}
                                            className={styles.select_item}
                                        >
                                            {ele.label}
                                        </li>
                                    )
                                })}
                            </div>
                        </ScrollArea>
                    </div>
                </Popover.Dropdown>
            </Popover>

            <Radio.Group
                label="Request method"
                mt="xs"
                value={method}
                onChange={handleRadioMethod}
            >
                <Radio value="GET" label="GET" />
                <Radio value="POST" label="POST" />
                <Radio value="PUT" label="PUT" />
                <Radio value="DELETE" label="DELETE" />
            </Radio.Group>
            <Text
                fw={700}
                mt="xs"
                fz={16}
            >
                Headers
            </Text>
            {
                headers.map((ele, index) => {
                    return (
                        <Grid
                            grow
                            align="flex-end"
                            key={index}
                            mt="xs"
                        >
                            <Grid.Col span={5}>
                                <TextInput
                                    label="Name"
                                    placeholder="Name (paste JSON here to auto-fill)"
                                    name="key"
                                    value={ele.key}
                                    onChange={handleChangeHeaders(ele.id)}
                                    onPaste={index === 0 ? handlePasteHeaders : undefined}
                                />
                            </Grid.Col>
                            <Grid.Col span={5} >
                                <VariableSelectWrite
                                    dataState={ele.value}
                                    name="value"
                                    placeholder="Value"
                                    onValueChange={handleChangeHeaders(ele.id)}
                                />
                            </Grid.Col>
                            <Grid.Col span={2}>
                                <Button
                                    w="100%"
                                    color="red"
                                    onClick={() => handleDeleteHeaders(ele.id)}
                                >
                                    <IconTrash size={20} />
                                </Button>
                            </Grid.Col>
                        </Grid>
                    )
                })
            }
            <Button
                rightIcon={<IconPlus size={16} />}
                mt="sm"
                onClick={handleAddHeaders}
            >
                Add
            </Button>
            <Text
                fw={700}
                mt="xs"
                fz={16}
            >
                Cookies
            </Text>
            {
                cookies.map((ele, index) => {
                    return (
                        <Grid
                            grow
                            align="flex-end"
                            key={index}
                            mt="xs"

                        >
                            <Grid.Col span={5}>
                                <TextInput
                                    label="Name"
                                    placeholder="Name (paste JSON here to auto-fill)"
                                    name="key"
                                    value={ele.key}
                                    onChange={handleChangeCookies(ele.id)}
                                    onPaste={index === 0 ? handlePasteCookies : undefined}
                                />
                            </Grid.Col>
                            <Grid.Col span={5} >
                                <VariableSelectWrite
                                    dataState={ele.value}
                                    name="value"
                                    placeholder="Value"
                                    onValueChange={handleChangeCookies(ele.id)}
                                />
                            </Grid.Col>
                            <Grid.Col span={2}>
                                <Button
                                    w="100%"
                                    color="red"
                                    onClick={() => handleDeleteCookies(ele.id)}
                                >
                                    <IconTrash size={20} />
                                </Button>
                            </Grid.Col>
                        </Grid>
                    )
                })
            }
            <Button
                rightIcon={<IconPlus size={16} />}
                mt="sm"
                onClick={handleAddCookies}
            >
                Add
            </Button>
            {
                (method !== 'GET' ? (
                    <>
                        <Select
                            mt="xs"
                            label="Select content Type"
                            defaultValue="application/json"
                            value={contentType}
                            onChange={handleSelectContentType}
                            data={[
                                { value: "application/json", label: "JSON" },
                                { value: "text/plain", label: "Text" },
                                { value: "text/html", label: "HTML" },
                                { value: "text/xml", label: "XML" },
                                { value: "application/x-www-form-urlencoded", label: "Form URLEncoded" },
                                { value: "multipart/form-data", label: "Form Data" },
                                { value: "custom", label: "Custom" },
                            ]}
                        />
                        {
                            (contentType !== 'application/x-www-form-urlencoded' && contentType !== 'multipart/form-data') ? (
                                <VariableTextarea
                                    textareaRef={textareaRefBody}
                                    placeholder="Enter request body"
                                    label={'Body - ' + contentType}
                                    dataState={dataString}
                                    handleData={onChangeBody}
                                    handleSelect={onChangeBody}
                                />
                            ) : (null)
                        }
                        <FormDataBody
                            ref={formBodyRef}
                            contentType={contentType}
                            onChange={onChangeBody}
                            dataState={dataString}
                        />
                    </>
                ) : (null))
            }


            <Tabs value={responseMode} onTabChange={setResponseMode} mt="sm">
                <Tabs.List>
                    <Tabs.Tab value="json">JSON Mapping</Tabs.Tab>
                    <Tabs.Tab value="simple">Simple Response</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="simple" pt="md">
                    <Text size="sm" color="dimmed" mb="md">
                        The full response will be stored in a variable of your choosing.
                    </Text>

                    <Select
                        label="Save Response To Variable"
                        placeholder="Select or create variable"
                        data={newData}
                        searchable
                        creatable
                        value={simpleResponseVariable}
                        onChange={setSimpleResponseVariable}
                        getCreateLabel={(query) => `+ Create ${query}`}
                        onCreate={(query) => {
                            const item = { id: uuid(), value: query, label: query, data: "", name: "" };
                            data.setNodeValue({
                                variables: [...data.nodeValue.variables, item]
                            });
                            return item;
                        }}
                        icon={leftSection}
                    />
                </Tabs.Panel>

                <Tabs.Panel value="json" pt="md">
                    <Text fw={500} mt="lg" mb="xs">Response Body Mapping</Text>
                    <Spoiler maxHeight={120} showLabel="Show more" hideLabel="Hide">
                        If the server response is in JSON format, you can store all or part of the response in variables using mappings. The entire response is available via the <Code>body</Code> path. For example: the value "value" can be extracted from JSON data using the path
                        <Code>response.result.field</Code>.
                        <Prism language="tsx" >{responseBodyMapping}</Prism>
                    </Spoiler>

                    {/* Body Variables */}
                    {outputBodyVariables.map((ele, index) => (
                        <Grid grow align="flex-end" key={index} mt="xs">
                            <Grid.Col span={5}>
                                <TextInput
                                    label="JSON path"
                                    placeholder="Path"
                                    name="key"
                                    value={ele.key}
                                    onChange={handleChangeBodyVariables(ele.id)}
                                />
                            </Grid.Col>
                            <Grid.Col span={5} >
                                <Select
                                    label="Output Variable"
                                    data={newData}
                                    placeholder="Select Variable"
                                    nothingFound="No created variables"
                                    searchable
                                    value={ele.value}
                                    onChange={(value) => handleSelectBodyVariable(value, ele.id)}
                                    creatable
                                    getCreateLabel={(query) => `+ Create ${query}`}
                                    onCreate={(query) => {
                                        const item = { id: uuid(), value: query, label: query, data: "", name: "" };
                                        data.setNodeValue({
                                            variables: [...data.nodeValue.variables, item]
                                        })
                                        return item;
                                    }}
                                    icon={leftSection}
                                />
                            </Grid.Col>
                            <Grid.Col span={2}>
                                <Button
                                    w="100%"
                                    color="red"
                                    onClick={() => handleDeleteBodyVariable(ele.id)}
                                >
                                    <IconTrash size={20} />
                                </Button>
                            </Grid.Col>
                        </Grid>
                    ))}

                    <Button
                        rightIcon={<IconPlus size={16} />}
                        mt="sm"
                        onClick={handleAddBodyVariable}
                    >
                        Add
                    </Button>

                    {/* Headers Variables */}
                    <Text fw={500} mt="lg" mb="xs">Response Headers Mapping</Text>
                    <Spoiler maxHeight={60} showLabel="Show example" hideLabel="Hide">
                        <Prism language="tsx">{responseHeadersMapping}</Prism>
                    </Spoiler>

                    {outputHeadersVariables.map((ele, index) => (
                        <Grid grow align="flex-end" key={index} mt="xs">
                            <Grid.Col span={5}>
                                <TextInput
                                    label="JSON path"
                                    placeholder="Path"
                                    name="key"
                                    value={ele.key}
                                    onChange={handleChangeHeaderVariable(ele.id)}
                                />
                            </Grid.Col>
                            <Grid.Col span={5} >
                                <Select
                                    label="Output Variable"
                                    data={newData}
                                    placeholder="Select Variable"
                                    nothingFound="No created variables"
                                    searchable
                                    value={ele.value}
                                    onChange={(value) => handleSelectHeaderVariable(value, ele.id)}
                                    creatable
                                    getCreateLabel={(query) => `+ Create ${query}`}
                                    onCreate={(query) => {
                                        const item = { id: uuid(), value: query, label: query, data: "", name: "" };
                                        data.setNodeValue({
                                            variables: [...data.nodeValue.variables, item]
                                        })
                                        return item;
                                    }}
                                    icon={leftSection}
                                />
                            </Grid.Col>
                            <Grid.Col span={2}>
                                <Button
                                    w="100%"
                                    color="red"
                                    onClick={() => handleDeleteHeaderVariable(ele.id)}
                                >
                                    <IconTrash size={20} />
                                </Button>
                            </Grid.Col>
                        </Grid>
                    ))}

                    <Button
                        rightIcon={<IconPlus size={16} />}
                        mt="sm"
                        onClick={handleAddHeaderVariable}
                    >
                        Add
                    </Button>
                    <Text
                        fw={700}
                        mt="xs"
                        fz={16}
                    >
                        Response cookies mapping
                    </Text>
                    <Spoiler maxHeight={80} showLabel="Show more" hideLabel="Hide">
                        Response cookies will be converted to JSON-format. For example:
                        <Prism language="tsx" >{responseCookiesMapping}</Prism>
                    </Spoiler>
                    {
                        outputCookiesVariables.map((ele, index) => {
                            return (
                                <Grid
                                    grow
                                    align="flex-end"
                                    key={index}
                                    mt="xs"
                                >
                                    <Grid.Col span={5}>
                                        <TextInput
                                            label="JSON path"
                                            placeholder="Path"
                                            name="key"
                                            value={ele.key}
                                            onChange={handleChangeCookieVariable(ele.id)}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={5} >
                                        <Select
                                            label="Output Variable"
                                            data={newData}
                                            placeholder="Select Variable"
                                            nothingFound="No created variables"
                                            searchable
                                            value={ele.value}
                                            onChange={(value) => handleSelectCookieVariable(value, ele.id)}
                                            creatable
                                            getCreateLabel={(query) => `+ Create ${query}`}
                                            onCreate={(query) => {
                                                const item = { id: uuid(), value: query, label: query, data: "", name: "" };
                                                data.setNodeValue({
                                                    variables: [...data.nodeValue.variables, item]
                                                })
                                                return item;
                                            }}
                                            icon={leftSection}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={2}>
                                        <Button
                                            w="100%"
                                            color="red"
                                            onClick={() => handleDeleteCookieVariable(ele.id)}
                                        >
                                            <IconTrash size={20} />
                                        </Button>
                                    </Grid.Col>
                                </Grid>
                            )
                        })
                    }
                    <Button
                        rightIcon={<IconPlus size={16} />}
                        mt="sm"
                        onClick={handleAddCookieVariable}
                    >
                        Add
                    </Button>
                </Tabs.Panel>
            </Tabs>
        </>
    )
}
export default SetHttpRequest


